import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { AreaChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area } from 'recharts';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';

const UsageChart = ({ title, type, color, data }) => {
   if (!data) {
    // If data is undefined or null, render a placeholder or message
    return (
      <Card sx={{ height: 460 }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h6" component="h3" sx={{textAlign:"left", width:'100%'}}>
          {title}
        </Typography>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.5, ease: "easeInOut" }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No data...
          </Typography>
        </motion.div>
      </CardContent>
    </Card>
    );
  }
  // Check if the data is valid and format it
  const formattedData = data.map((point) => ({
    time: new Date(point.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    value: point.average,
    timestamp: new Date(point.timestamp).getTime(),
  }));

  const currentValue = formattedData[formattedData.length - 1]?.value || 0;

  const formatTooltip = (value, name) => {
    const unit = type === 'network' ? 'MB/s' : type === 'disk' ? 'IOPS' : '%';
    return [`${value.toFixed(1)}${unit}`, title];
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h3">
            {title}
          </Typography>
          <Box textAlign="right">
            <Typography variant="h4" fontWeight="bold" sx={{ color }}>
              {currentValue.toFixed(1)}{type === 'network' ? 'MB/s' : type === 'disk' ? 'IOPS' : '%'}
            </Typography>
            <Typography variant="caption" color="text.secondary">Current</Typography>
          </Box>
        </Box>
        
        <Box sx={{ height: 300, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={formattedData}>
              <defs>
                <linearGradient id={`gradient-${type}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={color} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="time" 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#666' }}
                interval="preserveStartEnd"
              />
              <YAxis 
                domain={[0, type === 'network' ? 200 : type === 'disk' ? 1000 : 100]}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#666' }}
              />
              <Tooltip 
                formatter={formatTooltip}
                labelStyle={{ color: '#333' }}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                fill={`url(#gradient-${type})`}
                dot={false}
                activeDot={{ r: 4, fill: color }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
        
        <Box display="flex" justifyContent="space-between" mt={2}>
          <Typography variant="caption" color="text.secondary">
            Last 24 hours
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Auto-refresh: 1 min
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
UsageChart.propTypes = {
  title: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['cpu', 'memory', 'network', 'disk']).isRequired,
  color: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      timestamp: PropTypes.number.isRequired,
      average: PropTypes.number.isRequired,
    })
  ).isRequired,
};

export default UsageChart;
